import { useState, type FormEvent } from 'react'
import { AlertCircle, KeyRound } from 'lucide-react'
import { Button } from '@/ui/Button'
import { ApiError } from '@/lib/api'
import { useAdminSession } from './AdminSessionProvider'

/**
 * Passcode prompt, in the spirit of the original app's lock icon.
 *
 * Two differences from that one, both deliberate: the passcode is verified by
 * the Worker rather than compared in the browser, and repeated wrong attempts
 * are rate limited server-side — which is the only thing that makes a
 * memorable passcode defensible.
 */
export function UnlockDialog({ onClose, onUnlocked }: { onClose: () => void; onUnlocked: () => void }) {
  const { unlock, configured } = useAdminSession()
  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await unlock(passcode)
      onUnlocked()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not unlock.')
      setPasscode('')
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-[var(--radius-panel)] bg-[var(--surface-raised)] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600">
          <KeyRound className="h-7 w-7" aria-hidden="true" />
        </div>

        <h2 id="unlock-title" className="text-center text-xl font-bold tracking-tight">
          Admin access
        </h2>

        {configured ? (
          <>
            <p className="mt-1 text-center text-[0.9375rem] text-[var(--text-secondary)]">
              Enter the passcode to edit workflows.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label htmlFor="admin-passcode" className="sr-only">
                  Admin passcode
                </label>
                <input
                  id="admin-passcode"
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  autoFocus
                  autoComplete="current-password"
                  className="min-h-touch w-full rounded-2xl border border-[var(--border-input)] bg-[var(--surface-raised)] px-4 text-center outline-none focus:border-brand-600"
                />
              </div>

              {error !== null && (
                <p
                  role="alert"
                  className="flex items-start gap-2 rounded-2xl bg-bad-50 p-3 text-sm text-bad-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {error}
                </p>
              )}

              <div className="flex gap-3">
                <Button type="button" variant="ghost" fullWidth onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" fullWidth busy={busy} disabled={passcode === ''}>
                  Unlock
                </Button>
              </div>
            </form>
          </>
        ) : (
          // Says the actual problem instead of showing a box that cannot work.
          <>
            <p className="mt-2 text-center text-[0.9375rem] text-[var(--text-secondary)]">
              No admin passcode has been set for this deployment yet. Set one with:
            </p>
            <pre className="mt-3 overflow-x-auto rounded-2xl bg-[var(--surface-sunken)] p-3 text-xs">
              npm run set:passcode
            </pre>
            <Button variant="ghost" fullWidth className="mt-4" onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
